/**
 * API Strategic Planning Service Implementation
 * 
 * This service provides methods for:
 * - Managing planning cycles
 * - Creating and tracking strategic goals
 * - Managing initiatives and milestones
 * - Collecting and analyzing feedback
 * 
 * @example
 * const service = new ApiStrategicPlanningService(config.apiUrl);
 * const cycles = await service.getPlanningCycles();
 */

import { BaseService } from '../base/BaseService';
import {
  IStrategicPlanningService,
  CascadeContextResponse,
  ParentGoalOptionsResponse,
  CascadeSummaryResponse
} from '../../interfaces/IStrategicPlanningService';
import {
  PlanningCycle,
  StrategicGoal,
  Initiative,
  Milestone,
  FeedbackLoop,
  CreatePlanningCycleDto,
  CreateGoalDto,
  CreateInitiativeDto,
  CreateMilestoneDto,
  FeedbackDto,
  CycleStatus,
  ProgressReport
} from '../../../types/strategic-planning';
import { api } from '../../api';

export class ApiStrategicPlanningService extends BaseService implements IStrategicPlanningService {
  constructor(apiUrl: string) {
    super(apiUrl);
  }

  /**
   * Get all planning cycles, optionally filtered by church
   */
  async getPlanningCycles(churchId?: string): Promise<PlanningCycle[]> {
    try {
      const params = churchId ? { church_id: churchId } : {};
      const response = await api.get('/planning/planning-cycles/', { params });
      return response.data.results || response.data;
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error fetching planning cycles:', error);
      throw error;
    }
  }

  /**
   * Initiate a new planning cycle
   */
  async initiatePlanningCycle(data: CreatePlanningCycleDto): Promise<PlanningCycle> {
    try {
      const payload = {
        church: data.churchId,
        name: data.name,
        description: data.description,
        start_date: data.startDate,
        end_date: data.endDate,
        leadership_participants: data.leadershipParticipantIds || [],
        team_participants: data.teamParticipantIds || []
      };
      const response = await api.post(`/planning/planning-cycles/`, payload);
      return response.data;
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error creating planning cycle:', error);
      throw error;
    }
  }

  /**
   * Get the current status of a planning cycle
   */
  async getPlanningCycleStatus(cycleId: string): Promise<CycleStatus> {
    try {
      const response = await api.get(`/planning/planning-cycles/${cycleId}/status/`);
      return response.data;
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error fetching cycle status:', error);
      throw error;
    }
  }

  /**
   * Advance to the next phase of the planning cycle
   */
  async advancePhase(cycleId: string, outputs: any): Promise<void> {
    try {
      await api.post(`/planning/planning-cycles/${cycleId}/advance_phase/`, {
        outputs
      });
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error advancing phase:', error);
      throw error;
    }
  }

  /**
   * Generate a progress report for the planning cycle
   */
  async generateProgressReport(cycleId: string): Promise<ProgressReport> {
    try {
      const response = await api.post(`/planning/planning-cycles/${cycleId}/generate_report/`);
      return response.data;
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error generating progress report:', error);
      throw error;
    }
  }

  /**
   * Get strategic goals, optionally filtered by planning cycle
   */
  async getStrategicGoals(planningCycleId?: string): Promise<StrategicGoal[]> {
    try {
      const params = planningCycleId ? { planning_cycle: planningCycleId } : {};
      const response = await api.get(`/planning/goals/`, { params });
      return response.data.results || response.data;
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error fetching strategic goals:', error);
      throw error;
    }
  }

  /**
   * Create a new strategic goal
   */
  async createStrategicGoal(data: CreateGoalDto): Promise<StrategicGoal> {
    try {
      const payload = {
        planning_cycle: data.planningCycleId,
        category: data.category,
        title: data.title,
        description: data.description,
        rationale: data.rationale,
        specific: data.specific,
        measurable: data.measurable,
        achievable: data.achievable,
        relevant: data.relevant,
        time_bound: data.timeBound,
        priority: data.priority,
        target_date: data.targetDate,
        target_value: data.targetValue,
        resource_requirements: data.resourceRequirements,
        related_assessments: data.relatedAssessmentIds || []
      };
      const response = await api.post(`/planning/goals/`, payload);
      return response.data;
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error creating strategic goal:', error);
      throw error;
    }
  }

  /**
   * Update goal metrics
   */
  async updateGoalMetrics(goalId: string, metrics: any): Promise<StrategicGoal> {
    try {
      const response = await api.patch(`/planning/goals/${goalId}/update_metrics/`, {
        metrics
      });
      return response.data;
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error updating goal metrics:', error);
      throw error;
    }
  }

  /**
   * Get initiatives, optionally filtered by goal
   */
  async getInitiatives(goalId?: string): Promise<Initiative[]> {
    try {
      const params = goalId ? { strategic_goal: goalId } : {};
      const response = await api.get(`/planning/initiatives/`, { params });
      return response.data.results || response.data;
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error fetching initiatives:', error);
      throw error;
    }
  }

  /**
   * Create a new initiative
   */
  async createInitiative(data: CreateInitiativeDto): Promise<Initiative> {
    try {
      const payload = {
        strategic_goal: data.strategicGoalId,
        title: data.title,
        description: data.description,
        start_date: data.startDate,
        end_date: data.endDate,
        leader: data.leaderId,
        team_members: data.teamMemberIds || [],
        estimated_budget: data.estimatedBudget,
        dependencies: data.dependencies || [],
        risks: data.risks,
        mitigation_strategies: data.mitigationStrategies,
        related_assessments: data.relatedAssessmentIds || []
      };
      const response = await api.post(`/planning/initiatives/`, payload);
      return response.data;
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error creating initiative:', error);
      throw error;
    }
  }

  /**
   * Update initiative progress
   */
  async updateInitiativeProgress(initiativeId: string, percentage: number): Promise<Initiative> {
    try {
      const response = await api.patch(`/planning/initiatives/${initiativeId}/update_progress/`, {
        progress_percentage: percentage
      });
      return response.data;
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error updating initiative progress:', error);
      throw error;
    }
  }

  /**
   * Get milestones, optionally filtered by initiative
   */
  async getMilestones(initiativeId?: string): Promise<Milestone[]> {
    try {
      const params = initiativeId ? { initiative: initiativeId } : {};
      const response = await api.get(`/planning/milestones/`, { params });
      return response.data.results || response.data;
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error fetching milestones:', error);
      throw error;
    }
  }

  /**
   * Create a new milestone
   */
  async createMilestone(data: CreateMilestoneDto): Promise<Milestone> {
    try {
      const payload = {
        initiative: data.initiativeId,
        title: data.title,
        description: data.description,
        due_date: data.dueDate,
        deliverables: data.deliverables,
        responsible_party: data.responsiblePartyId,
        order: data.order
      };
      const response = await api.post(`/planning/milestones/`, payload);
      return response.data;
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error creating milestone:', error);
      throw error;
    }
  }

  /**
   * Complete a milestone with evidence
   */
  async completeMilestone(milestoneId: string, evidence: any): Promise<Milestone> {
    try {
      const response = await api.post(`/planning/milestones/${milestoneId}/complete/`, {
        evidence_of_completion: evidence
      });
      return response.data;
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error completing milestone:', error);
      throw error;
    }
  }

  /**
   * Collect feedback for a planning cycle
   */
  async collectFeedback(cycleId: string, feedback: FeedbackDto): Promise<FeedbackLoop> {
    try {
      const payload = {
        planning_cycle: cycleId,
        feedback_type: feedback.feedbackType,
        phase: feedback.phase,
        content: feedback.content,
        tags: feedback.tags || []
      };
      const response = await api.post(`/planning/feedback/`, payload);
      return response.data;
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error collecting feedback:', error);
      throw error;
    }
  }

  /**
   * Get feedback for a planning cycle
   */
  async getFeedback(cycleId: string): Promise<FeedbackLoop[]> {
    try {
      const response = await api.get(`/planning/feedback/`, {
        params: { planning_cycle: cycleId }
      });
      return response.data.results || response.data;
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error fetching feedback:', error);
      throw error;
    }
  }

  // ==========================================
  // Leadership Cascade Methods
  // ==========================================

  /**
   * Get leadership cascade context for the current user.
   *
   * Returns information about:
   * - User's leadership level
   * - What priority levels they can set
   * - What leadership levels they can view/create for
   */
  async getCascadeContext(churchId?: string): Promise<CascadeContextResponse> {
    try {
      const params = churchId ? { church_id: churchId } : {};
      const response = await api.get('/planning/goals/cascade_context/', { params });
      return response.data;
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error fetching cascade context:', error);
      throw error;
    }
  }

  /**
   * Get available parent goals for creating a new goal.
   *
   * Returns goals that the user can align their new goal with,
   * based on their leadership level.
   */
  async getParentGoalOptions(planningCycleId: string): Promise<ParentGoalOptionsResponse> {
    try {
      const response = await api.get('/planning/goals/parent_options/', {
        params: { planning_cycle_id: planningCycleId }
      });
      return response.data;
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error fetching parent options:', error);
      throw error;
    }
  }

  /**
   * Get cascade hierarchy summary for a specific goal.
   */
  async getCascadeSummary(goalId: string): Promise<CascadeSummaryResponse> {
    try {
      const response = await api.get(`/planning/goals/${goalId}/cascade_summary/`);
      return response.data;
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error fetching cascade summary:', error);
      throw error;
    }
  }

  /**
   * Approve cascade alignment for a goal.
   * Only Senior Leadership can approve cascades.
   */
  async approveCascade(goalId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post(`/planning/goals/${goalId}/approve_cascade/`);
      return response.data;
    } catch (error) {
      console.error('[ApiStrategicPlanningService] Error approving cascade:', error);
      throw error;
    }
  }
}